/**
 * Módulo F-22 AT 2026 - Régimen 14D3 (Pro PYME)
 * Exporta todas las secciones y configuración del formulario.
 */
import sociosDividendosService from '../../../../../services/sociosDividendosService';
import { getVentasNetas, getComprasNetas, getArriendosPagados, getIDPCAT, getPPMPagados, getRemuneracionesBrutas, getRemuneracionesAdeudadas, getHonorariosBrutos, getHonorariosAdeudados, getGastosGenerales } from '../../../../../services/declaracionRenta/f22Service';

export { createInitialState } from './initialState';
export { recalculate } from './formulas';
export { default as AntecedentesSection } from './sections/AntecedentesSection';
export { default as CreditoIPESection } from './sections/CreditoIPESection';
export { default as BaseImponibleSection } from './sections/BaseImponibleSection';
export { default as RAIFinalSection } from './sections/RAIFinalSection';
export { default as CPTSimplificadoSection } from './sections/CPTSimplificadoSection';
export { default as RTRESection } from './sections/RTRESection';
export { default as R17Section } from './sections/R17Section';
export { default as R18Section } from './sections/R18Section';
export { default as R19Section } from './sections/R19Section';
export { default as R20Section } from './sections/R20Section';
export { default as R21Section } from './sections/R21Section';

/**
 * Data loaders: fuentes de datos externas que se cargan en paralelo.
 * Cada entrada: key (id), loader (fetch), apply (inyecta datos al form).
 */
export const dataLoaders = [
  {
    key: 'socios',
    loader: (idCompany, token) => sociosDividendosService.getSocios(idCompany, token),
    apply: (formData, sociosApi) => {
      if (!sociosApi || sociosApi.length === 0) return formData;
      const existingSocios = formData.antecedentes?.socios || [];
      const socios = sociosApi.map((s) => {
        const existing = existingSocios.find((fs) => fs.rut === s.rut) || {};
        return {
          nombre: s.nombre || '',
          rut: s.rut || '',
          participacion: parseFloat(s.porcentaje_participacion) || 0,
          capitalHistorico: parseInt(s.monto_clp_aportado) || 0,
          capitalReajustado: parseInt(s.reajuste_31_12_2019) || 0,
          fechaRetiro: existing.fechaRetiro || '',
          retiroHistorico: existing.retiroHistorico || 0,
        };
      });
      return { ...formData, antecedentes: { ...formData.antecedentes, socios } };
    },
  },
  {
    key: 'ventasNetas',
    // anioComercial 2025 (AT 2026): suma facturas_venta + boletas_sii + comprobantes
    loader: (idCompany) => getVentasNetas(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.ventasNetasTotal || 0;
      return {
        ...formData,
        antecedentes: {
          ...formData.antecedentes,
          ventasNoRelacionadasNeto: total,
        },
      };
    },
  },
  {
    key: 'comprasNetas',
    // anioComercial 2025 (AT 2026): suma monto_neto de facturas_compra
    loader: (idCompany) => getComprasNetas(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.comprasNetasTotal || 0;
      return {
        ...formData,
        antecedentes: {
          ...formData.antecedentes,
          comprasNeto: total,
          comprasPagado: total,
        },
      };
    },
  },
  {
    key: 'arriendos',
    loader: (idCompany) => getArriendosPagados(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.arriendosPagadosTotal || 0;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, arriendos: total },
      };
    },
  },
  {
    key: 'ppmPagados',
    loader: (idCompany) => getPPMPagados(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.ppmTotal || 0;
      const reajuste = result.data.reajustePPM || 0;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, ppmHistorico: total, reajustePPMHistorico: reajuste },
      };
    },
  },
  {
    key: 'idpcAt2025',
    // AT 2025: cod_91 de datos_renta_2025_sii
    loader: (idCompany) => getIDPCAT(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      return {
        ...formData,
        antecedentes: {
          ...formData.antecedentes,
          pagoIDPCAT2025: result.data.idpcTotal || 0,
          cptsAl31Dic2024: result.data.cpts1545 || 0,
          remanentesAnteriores: {
            ...formData.antecedentes.remanentesAnteriores,
            RAI: result.data.rai1484 || 0,
            REX_INR: result.data.rexINR1493 || 0,
            SAC_noSujetoConDev: result.data.sacNoSujConDev1564 || 0,
          },
        },
      };
    },
  },
  {
    key: 'gastosGenerales',
    loader: (idCompany) => getGastosGenerales(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, gastosGenerales: result.data.gastosGeneralesTotal || 0 },
      };
    },
  },
  {
    key: 'honorariosBrutos',
    loader: (idCompany) => getHonorariosBrutos(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, honorariosBruto: result.data.honorariosBrutoTotal || 0 },
      };
    },
  },
  {
    key: 'honorariosAdeudados',
    loader: (idCompany) => getHonorariosAdeudados(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, honorariosAdeudado: result.data.honoariosAdeudadosTotal || 0 },
      };
    },
  },
  {
    key: 'remuneracionesAdeudadas',
    loader: (idCompany) => getRemuneracionesAdeudadas(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.remuneracionesAdeudadasTotal || 0;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, remuneracionesAdeudado: total },
      };
    },
  },
  {
    key: 'remuneracionesBrutas',
    loader: (idCompany) => getRemuneracionesBrutas(idCompany, 2025),
    apply: (formData, result) => {
      if (!result?.data) return formData;
      const total = result.data.remuneracionesTotal || 0;
      return {
        ...formData,
        antecedentes: { ...formData.antecedentes, remuneracionesBruto: total },
      };
    },
  },
  {
    key: 'dividendos',
    // anioComercial 2025 (AT 2026)
    loader: (idCompany, token) => sociosDividendosService.getDividendos(idCompany, 2025, token),
    apply: (formData, dividendosApi) => {
      if (!dividendosApi || dividendosApi.length === 0) return formData;
      const socios = formData.antecedentes?.socios || [];
      const retiros = dividendosApi.map((d) => {
        const socio = socios.find((s) => s.rut === d.rut_receptor);
        return {
          nombre: socio?.nombre || d.rut_receptor,
          rut: d.rut_receptor,
          fechaRetiro: d.fecha_retiro ? d.fecha_retiro.substring(0, 10) : '',
          retiroHistorico: parseInt(d.monto_c5) || 0,
          retiroActualizado: parseInt(d.reajuste_c5_31_12) || 0,
        };
      });
      return { ...formData, antecedentes: { ...formData.antecedentes, retiros } };
    },
  },
];

export const SECTIONS = [
  { id: 'antecedentes', label: 'Antecedentes', component: 'AntecedentesSection' },
  { id: 'credito-ipe', label: 'Crédito IPE', component: 'CreditoIPESection' },
  { id: 'base-imponible', label: 'Base Imponible', component: 'BaseImponibleSection' },
  { id: 'rai-final', label: 'RAI Final', component: 'RAIFinalSection' },
  { id: 'rtre', label: 'RTRE', component: 'RTRESection' },
  { id: 'cpt-simplificado', label: 'CPT Simplificado', component: 'CPTSimplificadoSection' },
  { id: 'r17', label: 'R17', component: 'R17Section' },
  { id: 'r18', label: 'R18', component: 'R18Section' },
  { id: 'r19', label: 'R19', component: 'R19Section' },
  { id: 'r20', label: 'R20', component: 'R20Section' },
  { id: 'r21', label: 'R21', component: 'R21Section' },
];
